/**
 * THE SINGLE ATTRACTOR LAW — FIRST BLOOD §4.1:
 * "one BREATHE/PULSE-RING per screen, ever."
 *
 * This registry is the framework-agnostic core. React wires it up via
 * IdleAttractorScope/BreathingAttractor (./index.tsx).
 *
 * Dev mode: registering a second live attractor inside one scope THROWS,
 * naming both attractors. Production: the second registration is ignored
 * with a console.warn — the law holds, the app survives.
 */

import { isDevEnv } from "../types.js";

export class SingleAttractorViolation extends Error {
  readonly scopeName: string;
  readonly liveAttractor: string;
  readonly rejectedAttractor: string;

  constructor(scopeName: string, live: string, rejected: string) {
    super(
      `[juice] Single Attractor Law violated in scope "${scopeName}": ` +
        `attractor "${live}" is already live; refusing to register "${rejected}". ` +
        `Exactly one element may breathe per screen (FIRST BLOOD §4.1).`,
    );
    this.name = "SingleAttractorViolation";
    this.scopeName = scopeName;
    this.liveAttractor = live;
    this.rejectedAttractor = rejected;
  }
}

export interface AttractorRegistryOptions {
  scopeName?: string;
  /** Overrides isDevEnv() — injectable for tests. */
  isDev?: boolean;
  warn?: (message: string) => void;
}

export interface AttractorRegistration {
  /** True when this registration holds the (single) slot. */
  readonly live: boolean;
  /** Frees the slot iff this registration holds it. Idempotent. */
  release(): void;
}

export class AttractorRegistry {
  readonly scopeName: string;
  private readonly isDev: boolean;
  private readonly warn: (message: string) => void;
  private activeName: string | null = null;
  private activeToken: symbol | null = null;

  constructor(opts: AttractorRegistryOptions = {}) {
    this.scopeName = opts.scopeName ?? "screen";
    this.isDev = opts.isDev ?? isDevEnv();
    this.warn =
      opts.warn ??
      ((message) => {
        console.warn(message);
      });
  }

  get active(): string | null {
    return this.activeName;
  }

  register(name: string): AttractorRegistration {
    if (this.activeName !== null) {
      if (this.isDev) {
        throw new SingleAttractorViolation(this.scopeName, this.activeName, name);
      }
      this.warn(
        `[juice] Single Attractor Law: scope "${this.scopeName}" already has live attractor ` +
          `"${this.activeName}" — ignoring "${name}".`,
      );
      return { live: false, release: () => undefined };
    }
    const token = Symbol(name);
    this.activeName = name;
    this.activeToken = token;
    const registry = this;
    return {
      get live() {
        return registry.activeToken === token;
      },
      release() {
        if (registry.activeToken === token) {
          registry.activeName = null;
          registry.activeToken = null;
        }
      },
    };
  }
}
